import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'
import signatureStyles from '../../formParts/signatureSentinel/styles.module.scss'
import responsiveSignatureStyles from '../../formParts/signatureSentinel/responsiveStyles.module.scss'

import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import SignatureCanvas from 'react-signature-canvas'
import axios from 'axios'
import { SubmitButton } from '..'
import { useParams } from 'react-router-dom'
import { trackSubmitApplication } from '../../../helpers/handleTracking'
import { useClaimData, useUpdateClaimData } from '../../../claimProvider'
import Previewer from '../previewer'

interface sigProps {
    additionalAction?: () => void | null
    extraPayload?: any
    disclaimer?: any
    children?: React.ReactNode
    stepData?: any
}

export const SignaturePad = ({ extraPayload, additionalAction, disclaimer, children, stepData }: sigProps) => {
    const signCanvas = React.useRef<SignatureCanvas>(null) as React.MutableRefObject<any>
    const [sigData, setSigData] = useState('')
    const [acceptTermsChecked, setAcceptTermsChecked] = useState(false)
    const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false)
    const claimData = useClaimData()
    const updateClaim = useUpdateClaimData()
    const { token } = useParams()

    useEffect(() => {
        if (stepData) {
            console.log('StepData updated:', stepData)
            setAcceptTermsChecked(stepData.acceptTerms === 'yes')
            setPrivacyPolicyChecked(stepData.privacyPolicyConsent === 'yes')
        }
    }, [stepData])

    useEffect(() => {
        console.log('Checkbox states:', { acceptTermsChecked, privacyPolicyChecked })
    }, [acceptTermsChecked, privacyPolicyChecked])

    const saveSign = () => {
        signCanvas.current.on()
        let x = signCanvas.current.toDataURL('image/png')
        setSigData(x)
    }

    const clearSign = (e: React.MouseEvent) => {
        e.preventDefault()
        clearSigPad()
    }

    const clearSigPad = () => {
        signCanvas.current.clear()
        signCanvas.current.on()
        setSigData('')
    }

    const submitSignature = () => {
        if (sigData.length < 1) {
            Report.failure('Please Sign', 'Please enter your signature onto the signature pad to continue.', 'OK')
            return
        }

        if (!acceptTermsChecked || !privacyPolicyChecked) {
            Report.failure('Please Accept Terms', 'Please accept both terms and privacy policy to continue.', 'OK')
            return
        }

        Loading.circle('Signing Documents')
        let payload = {
            token,
            type: '',
            sigData,
        }

        if (extraPayload && Object.keys(extraPayload).length) {
            // console.log("extra payload: ", extraPayload);
            // console.log("extra payload: ", Object.keys(extraPayload).length);
            payload = { ...payload, ...extraPayload }
        }

        let signEndpoint = process.env.REACT_APP_API_SIGN_URL
        if (payload.type === 'sentinelSignIL') signEndpoint = process.env.REACT_APP_API_SIGN_IL_URL
        if (payload.type === 'webform_IRRESPONSIBLE_BOSH') signEndpoint = process.env.REACT_APP_API_SIGN_IL_URL

        axios
            .post(`${signEndpoint}`, payload)
            .then(function (response) {
                console.log(response)
                Loading.remove()
                if (response.data !== 'SUCCESS') {
                    if (!response?.data?.success) {
                        Report.failure(
                            'An Error Occurred',
                            'We have encounterred an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                            'Okay'
                        )
                        return
                    } else {
                        updateClaim({
                            ...claimData,
                            openBankingUrl: response.data.shortLink,
                        })
                        return
                    }
                }
                // clearSigPad();
                if (additionalAction) additionalAction()

                trackSubmitApplication()
            })
            .catch(function (error) {
                console.log(error)
                Loading.remove()
                Report.failure(
                    'An Error Occurred',
                    'We have encounterred an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                    'Okay'
                )
                // if (additionalAction) additionalAction();
            })
        // Loading.remove();
        // if (additionalAction) additionalAction();
    }

    //<Previewer url={preview} />;
    return (
        <div className={styles.signaturePadContent}>
            <div className={`${styles.sigPadContainer}  `}>
                <p>Please use your mouse, finger or stylus to draw your signature below.</p>
                <div>
                    <SignatureCanvas
                        ref={signCanvas}
                        onEnd={saveSign}
                        clearOnResize={false}
                        penColor="grey"
                        canvasProps={{
                            width: window.innerWidth < 760 ? 350 : 760,
                            height: 300,
                            className: 'sigCanvas mf-masked mf-listen',
                        }}
                    />
                    <span onClick={clearSign}>Clear</span>
                </div>
            </div>
            <div>
                {children}
            </div>
            <div className={styles.buttonContainer}>
                <SubmitButton
                    text="Submit"
                    btnType="primary"
                    onClick={submitSignature}
                    disabled={!acceptTermsChecked || !privacyPolicyChecked}
                />
            </div>
            <div className={styles.compensationText}>
                <p className={styles.compensationLine1}>You could be owed compensation</p>
                <p className={styles.compensationLine2}>with an average value of</p>
                <p className={styles.amount}>£5,318*</p>
            </div>
            {disclaimer && (
                <p className={styles.signatureDisclaimer} style={{ textAlign: 'left' }}>
                    {disclaimer}
                </p>
            )}
            <p className={styles.signatureDisclaimer} style={{ textAlign: 'left' }}>
                *By continuing, you agree that Sentinel Legal will share your personal and financial information with
                Valid8 Financial Technologies Limited ("Valid8") for the sole purpose of securely verifying your
                eligibility for a claim. Valid8 is an FCA-regulated provider of financial data services. They will
                access your financial information in line with strict security standards, and only for verifying whether
                you may have a valid claim. You can read Valid8's full privacy policy here: Valid8 Privacy Policy. By
                clicking "I Agree", you consent to Sentinel Legal and Valid8 processing your data for claim verification
                purposes.
            </p>

            <div className={`${signatureStyles.documentsSection} ${responsiveSignatureStyles.documentsSection}`}>
                <h3 className={`${signatureStyles.documentsTitle} ${responsiveSignatureStyles.documentsTitle}`}>
                    Your Retainer & Client Care Documents
                </h3>
                <p className={`${signatureStyles.documentsSubtitle} ${responsiveSignatureStyles.documentsSubtitle}`}>
                    Please carefully review these documents before providing your signature. These documents form your
                    agreement with Sentinel Legal.
                </p>

                {claimData && (
                    <div
                        className={`${signatureStyles.documentsContainer} ${responsiveSignatureStyles.documentsContainer}`}
                    >
                        <div className={`${signatureStyles.documentItem} ${responsiveSignatureStyles.documentItem}`}>
                            <h4>Conditional Fee Agreement (CFA)</h4>
                            <p>
                                This agreement sets out the terms of our no-win, no-fee arrangement and explains how our
                                fees are calculated.
                            </p>
                            {claimData?.cfaUrl ? (
                                <Previewer url={claimData.cfaUrl} documentString={'View CFA Document'} />
                            ) : (
                                <p className={signatureStyles.documentUnavailable}>CFA document not yet available</p>
                            )}
                        </div>

                        <div className={`${signatureStyles.documentItem} ${responsiveSignatureStyles.documentItem}`}>
                            <h4>Letter of Authority (LOA)</h4>
                            <p>
                                This document authorizes Sentinel Legal to act on your behalf and communicate with your
                                finance provider.
                            </p>
                            {claimData?.loaUrl ? (
                                <Previewer url={claimData.loaUrl} documentString={'View LOA Document'} />
                            ) : (
                                <p className={signatureStyles.documentUnavailable}>LOA document not yet available</p>
                            )}
                        </div>

                        <div className={`${signatureStyles.documentItem} ${responsiveSignatureStyles.documentItem}`}>
                            <h4>Assignment of Funds</h4>
                            <p>
                                This document assigns your compensation funds to Sentinel Legal to cover legal fees and
                                disbursements in accordance with your Conditional Fee Agreement.
                            </p>
                            {claimData?.assignmentOfFundsUrl ? (
                                <Previewer url={claimData.assignmentOfFundsUrl} documentString={'View Assignment of Funds Document'} />
                            ) : (
                                <p className={signatureStyles.documentUnavailable}>Assignment of Funds document not yet available</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
